import { Component } from '@theme/component';

const STOREFRONT_API_VERSION = '2025-01';
const CARDS_SECTION = 'taxonomy-leaf-recommendation-cards';

const SEARCH_BY_TAXONOMY_LEAF = `
  query SearchByTaxonomyLeaf($query: String!, $first: Int!) {
    search(query: $query, first: $first, types: PRODUCT) {
      edges {
        node {
          ... on Product {
            id
            handle
          }
        }
      }
    }
  }
`;

const COLLECTION_BY_TAXONOMY = `
  query CollectionByTaxonomyLeaf($handle: String!, $filters: [ProductFilter!], $first: Int!) {
    collection(handle: $handle) {
      products(first: $first, filters: $filters) {
        edges {
          node {
            id
            handle
          }
        }
      }
    }
  }
`;

class TaxonomyLeafRelated extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRelated();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.serverRendered === 'true') {
      return;
    }

    if (!this.dataset.taxonomyLeaf) {
      this.classList.add('hidden');
      return;
    }

    if (this.dataset.useStorefront !== 'true') {
      this.classList.add('hidden');
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #loadRelated() {
    if (this.dataset.loaded === 'true' || this.dataset.serverRendered === 'true') return;

    const host = this.querySelector('[data-taxonomy-leaf-host]');
    if (!(host instanceof HTMLElement)) return;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    const { signal } = this.#activeFetch;

    try {
      const handles = await this.#fetchHandles(signal);
      if (!handles.length) {
        this.#hide();
        return;
      }

      const cardsHtml = await this.#fetchCards(handles, signal);
      if (!cardsHtml) {
        this.#hide();
        return;
      }

      const doc = new DOMParser().parseFromString(cardsHtml, 'text/html');
      const cards = doc.querySelector('.taxonomy-leaf-related__cards');

      if (!cards || cards.dataset.hasRecommendations !== 'true') {
        this.#hide();
        return;
      }

      host.replaceChildren(document.importNode(cards, true));
      host.removeAttribute('aria-busy');
      this.dataset.loaded = 'true';
      this.classList.remove('hidden');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Taxonomy leaf related products error:', error);
      this.#hide();
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {AbortSignal} signal
   * @returns {Promise<string[]>}
   */
  async #fetchHandles(signal) {
    const leaf = this.dataset.taxonomyLeaf;
    if (!leaf) return [];

    const filterAttempts = this.#buildFilterAttempts(leaf);
    for (const attempt of filterAttempts) {
      const handles = await this.#runStorefrontQuery(attempt, signal);
      if (handles.length) return handles;
    }

    return [];
  }

  /**
   * @param {string} leaf
   * @returns {Array<{ kind: 'search', query: string } | { kind: 'collection', handle: string, filters: object[] }>}
   */
  #buildFilterAttempts(leaf) {
    const escaped = leaf.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    /** @type {Array<{ kind: 'search', query: string } | { kind: 'collection', handle: string, filters: object[] }>} */
    const attempts = [
      { kind: 'search', query: `metafields.custom.taxonomy_leaf:"${escaped}"` },
      { kind: 'search', query: `product_metafield:"custom.taxonomy_leaf:${escaped}"` },
    ];

    if (leaf.startsWith('gid://shopify/TaxonomyCategory/')) {
      attempts.push({
        kind: 'collection',
        handle: 'all',
        filters: [{ category: { id: leaf } }],
      });
      attempts.push({
        kind: 'collection',
        handle: 'all',
        filters: [
          {
            taxonomyMetafield: {
              namespace: 'custom',
              key: 'taxonomy_leaf',
              value: leaf,
            },
          },
        ],
      });
    }

    attempts.push({
      kind: 'collection',
      handle: 'all',
      filters: [
        {
          productMetafield: {
            namespace: 'custom',
            key: 'taxonomy_leaf',
            value: leaf,
          },
        },
      ],
    });

    return attempts;
  }

  /**
   * @param {{ kind: 'search', query: string } | { kind: 'collection', handle: string, filters: object[] }} attempt
   * @param {AbortSignal} signal
   */
  async #runStorefrontQuery(attempt, signal) {
    const shop = this.dataset.shop;
    const token = this.dataset.storefrontToken;
    const maxProducts = Number.parseInt(this.dataset.maxProducts ?? '8', 10) || 8;
    const fetchCount = Math.min(maxProducts + 4, 24);

    if (!shop || !token) return [];

    const body =
      attempt.kind === 'search'
        ? {
            query: SEARCH_BY_TAXONOMY_LEAF,
            variables: { query: attempt.query, first: fetchCount },
          }
        : {
            query: COLLECTION_BY_TAXONOMY,
            variables: {
              handle: attempt.handle,
              filters: attempt.filters,
              first: fetchCount,
            },
          };

    const response = await fetch(`https://${shop}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      throw new Error(`Storefront API returned ${response.status}`);
    }

    const payload = await response.json();

    if (payload.errors?.length) {
      return [];
    }

    if (attempt.kind === 'search') {
      const edges = payload.data?.search?.edges ?? [];
      return edges
        .map((/** @type {{ node: { handle?: string } }} */ edge) => edge.node?.handle)
        .filter((/** @type {string | undefined} */ handle) => typeof handle === 'string' && handle.length > 0);
    }

    const edges = payload.data?.collection?.products?.edges ?? [];
    return edges
      .map((/** @type {{ node: { handle?: string } }} */ edge) => edge.node?.handle)
      .filter((/** @type {string | undefined} */ handle) => typeof handle === 'string' && handle.length > 0);
  }

  /**
   * @param {string[]} handles
   * @param {AbortSignal} signal
   * @returns {Promise<string | null>}
   */
  async #fetchCards(handles, signal) {
    const root = window.Shopify?.routes?.root ?? '/';
    const url = new URL(root, window.location.origin);
    url.searchParams.set('sections', CARDS_SECTION);
    url.searchParams.set('handles', handles.join(','));
    url.searchParams.set('exclude_product_id', this.dataset.productId ?? '');
    url.searchParams.set('limit', this.dataset.maxProducts ?? '8');
    url.searchParams.set('layout_type', this.dataset.layoutType ?? 'carousel');
    url.searchParams.set('columns_gap', this.dataset.columnsGap ?? '12');
    url.searchParams.set('icons_style', this.dataset.iconsStyle ?? 'arrow');
    url.searchParams.set('icons_shape', this.dataset.iconsShape ?? 'circle');
    url.searchParams.set('section_width', this.dataset.sectionWidth ?? 'page-width');

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) {
      throw new Error(`Section render returned ${response.status}`);
    }

    const data = await response.json();
    const sectionHtml = data[CARDS_SECTION];
    return typeof sectionHtml === 'string' ? sectionHtml : null;
  }

  #hide() {
    this.classList.add('hidden');
    const host = this.querySelector('[data-taxonomy-leaf-host]');
    host?.removeAttribute('aria-busy');
  }
}

if (!customElements.get('taxonomy-leaf-related')) {
  customElements.define('taxonomy-leaf-related', TaxonomyLeafRelated);
}
